import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { isHumanAgentInboxLive, takeOverBotConversation } from "@/api/inboxAgentApi";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import AppLayout from "@/components/feature/AppLayout";
import ConversationList from "./components/ConversationList";
import ChatThread from "./components/ChatThread";
import UserDetailsPanel from "./components/UserDetailsPanel";
import InboxResizableShell from "./components/InboxResizableShell";
import { useTickets } from "@/pages/tickets/hooks/useTickets";
import { useInboxConversations } from "./hooks/useInboxConversations";
import { useConversationThread } from "./hooks/useConversationThread";
import { useInboxRealtime } from "./hooks/useInboxRealtime";
import { setInboxSelectedConversationId } from "@/lib/inboxAlertSelection";
import {
  patchSlaMetaForAgentReply,
  patchSlaMetaForUserMessage,
} from "@/lib/slaMetaPatch";
import { ConversationThreadCache } from "./lib/ConversationThreadCache";

export default function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isBotMessagesPage =
    (location.pathname || "").replace(/\/$/, "").endsWith("/bot-chats");
  const requestedSessionId = new URLSearchParams(location.search).get("session");
  const { activeWorkspaceId, workspaces } = useWorkspace();
  const [selectedId, setSelectedId] = useState(null);
  const [activeCategory, setActiveCategory] = useState(isBotMessagesPage ? "bot-chats" : "all");
  const threadCacheRef = useRef(null);
  if (!threadCacheRef.current) {
    threadCacheRef.current = new ConversationThreadCache();
  }
  const threadCache = threadCacheRef.current;
  const { createTicketFromLiveChat } = useTickets(activeWorkspaceId);

  useEffect(() => () => threadCache.clear(), [threadCache]);

  useEffect(() => {
    if (isBotMessagesPage) {
      setActiveCategory("bot-chats");
      setSelectedId(null);
      return;
    }
    setActiveCategory((prev) => (prev === "bot-chats" || prev === "your-inbox" ? "all" : prev));
    setSelectedId(null);
  }, [isBotMessagesPage]);

  // Notification links and successful bot takeovers can target a session
  // directly, even when it is not present in the first conversation-list page.
  useEffect(() => {
    if (isBotMessagesPage) return;
    if (requestedSessionId) setSelectedId(requestedSessionId);
  }, [isBotMessagesPage, requestedSessionId]);

  const {
    conversations,
    inboxCategories,
    teamInboxes,
    loading: listLoading,
    error: listError,
    refresh: refreshInbox,
    patchConversation,
    loadMore: loadMoreConversations,
    loadingMore: loadingMoreConversations,
    hasMore: hasMoreConversations,
  } = useInboxConversations(activeCategory, { centralInbox: true });

  /** Bot Messages lives in the left rail — keep it out of Inbox Views. */
  const listViewCategories = useMemo(() => {
    if (isBotMessagesPage) return [];
    return (inboxCategories ?? []).filter((c) => c.id !== "bot-chats" && c.id !== "your-inbox");
  }, [inboxCategories, isBotMessagesPage]);

  const patchInboxSlaMeta = useCallback(
    (msg) => {
      if (isBotMessagesPage) return;
      if (!selectedId || !msg?.sentAt) return;
      if (msg.sender === "agent") {
        patchConversation(selectedId, (c) => ({
          ...c,
          ...(msg.assignee ? { assignee: msg.assignee, suggestedAssignee: null } : {}),
          slaMeta: patchSlaMetaForAgentReply(c.slaMeta, msg.sentAt),
        }));
      } else if (msg.sender === "user") {
        patchConversation(selectedId, (c) => ({
          ...c,
          slaMeta: patchSlaMetaForUserMessage(c.slaMeta, msg.sentAt),
        }));
      }
    },
    [selectedId, patchConversation, isBotMessagesPage],
  );

  const selectedWorkspaceHint = useMemo(
    () => conversations.find((item) => item.id === selectedId)?.workspaceId ?? null,
    [conversations, selectedId],
  );

  const {
    conversation,
    messages,
    loading: threadLoading,
    error: threadError,
    sendMessage,
    deleteMessage,
    reload: reloadThread,
    invalidateCache: invalidateThreadCache,
    applyThreadData,
    removeMessage,
    transferChat,
    resolveSession,
    reopenSession,
    appendMessage,
  } = useConversationThread(selectedId, null, {
    workspaceId: activeWorkspaceId,
    workspaceHint: selectedWorkspaceHint,
    centralInbox: true,
    onAfterMessageSent: patchInboxSlaMeta,
    threadCache,
  });

  const handleAfterTransfer = useCallback(() => {
    void refreshInbox({ silent: true });
  }, [refreshInbox]);

  const handleResolve = useCallback(async () => {
    await resolveSession();
    if (selectedId) {
      patchConversation(selectedId, (c) => ({ ...c, status: "resolved" }));
    }
    void refreshInbox({ silent: true });
  }, [resolveSession, refreshInbox, selectedId, patchConversation]);

  const handleReopen = useCallback(async () => {
    await reopenSession();
    if (selectedId) {
      patchConversation(selectedId, (c) => ({ ...c, status: "active" }));
    }
    void refreshInbox({ silent: true });
  }, [reopenSession, refreshInbox, selectedId, patchConversation]);

  const handleTakeOver = useCallback(async () => {
    const sessionId = selectedId;
    const sessionWorkspaceId = String(conversation?.workspaceId || "").trim();
    if (!sessionId) throw new Error("Select a bot conversation first.");
    if (!sessionWorkspaceId) {
      throw new Error("This conversation has no workspace scope and cannot be taken over safely.");
    }
    await takeOverBotConversation({
      sessionId,
      workspaceId: sessionWorkspaceId,
    });
    toast.success("Bot stopped. The conversation is now assigned to you.");
    navigate(`/chat?session=${encodeURIComponent(sessionId)}`);
  }, [selectedId, conversation?.workspaceId, navigate]);

  /** The customer replied after resolution — the backend already flipped it back to open. */
  const handleReopenedByCustomer = useCallback(
    (data) => {
      const sessionId = data?.session_id ?? selectedId;
      if (sessionId) {
        patchConversation(sessionId, (c) => ({ ...c, status: "active" }));
      }
      void refreshInbox({ silent: true });
    },
    [selectedId, patchConversation, refreshInbox],
  );

  const handleSelectConversation = useCallback((id) => {
    setSelectedId(id);
  }, []);

  // Auto-select first conversation once the list loads (live mode only)
  const [didAutoSelect, setDidAutoSelect] = useState(false);
  useEffect(() => {
    setDidAutoSelect(false);
  }, [isBotMessagesPage]);
  useEffect(() => {
    if (
      isHumanAgentInboxLive() &&
      !didAutoSelect &&
      !listLoading &&
      conversations.length > 0 &&
      !selectedId &&
      !requestedSessionId
    ) {
      setDidAutoSelect(true);
      setSelectedId(conversations[0].id);
    }
  }, [conversations, listLoading, selectedId, didAutoSelect, requestedSessionId]);

  const handleTicketCreated = useCallback(
    (ticket, warnings = []) => {
      if (!ticket?.id) return;
      setSelectedId(null);
      void refreshInbox({ silent: true });
      for (const w of warnings) {
        if (w) toast.warning(String(w));
      }
      navigate("/tickets", {
        state: {
          focusTicketId: ticket.id,
          conversationId: ticket.conversationId,
          ticketStatus: ticket.status || "open",
        },
      });
    },
    [navigate, refreshInbox],
  );

  const handleCategoryChange = useCallback(
    (id) => {
      if (isBotMessagesPage) return;
      setActiveCategory(id);
    },
    [isBotMessagesPage],
  );

  const handleAfterTeamInboxMove = useCallback(() => {
    void refreshInbox({ silent: true });
    invalidateThreadCache();
    void reloadThread({ silent: true, force: true });
  }, [refreshInbox, reloadThread, invalidateThreadCache]);

  const handleVisitorEmailUpdated = useCallback(() => {
    invalidateThreadCache();
    void reloadThread({ silent: true, force: true });
    void refreshInbox({ silent: true });
  }, [reloadThread, refreshInbox, invalidateThreadCache]);

  const handleFoldersChanged = useCallback(() => {
    void refreshInbox({ silent: true });
  }, [refreshInbox]);

  const workspacePickerList = useMemo(
    () =>
      (workspaces ?? []).map((w) => ({
        id: String(w.id ?? ""),
        name: String(w.name ?? w.slug ?? w.id ?? ""),
      })),
    [workspaces],
  );

  const handleRealtimeMessage = useCallback(
    (msg) => {
      if (!msg) return;
      appendMessage(msg);
      patchInboxSlaMeta(msg);
    },
    [appendMessage, patchInboxSlaMeta]
  );

  const handleThreadSync = useCallback(
    (data) => {
      applyThreadData(data);
    },
    [applyThreadData],
  );

  useEffect(() => {
    setInboxSelectedConversationId(selectedId);
    return () => setInboxSelectedConversationId(null);
  }, [selectedId]);

  useInboxRealtime(
    isHumanAgentInboxLive()
      ? {
          conversationId: selectedId,
          workspaceId: activeWorkspaceId,
          centralInbox: true,
          // Right after a switch `conversation` still holds the previous thread;
          // sending its workspace with the new session id would scope the fetch
          // to the wrong workspace.
          sessionWorkspaceId:
            conversation?.id === selectedId ? conversation?.workspaceId ?? null : null,
          onMessage: handleRealtimeMessage,
          onMessageDeleted: removeMessage,
          onThreadSync: handleThreadSync,
          onRefresh: reloadThread,
          onReopened: handleReopenedByCustomer,
        }
      : {
          conversationId: null,
          workspaceId: null,
          centralInbox: false,
          sessionWorkspaceId: null,
          onMessage: handleRealtimeMessage,
          onMessageDeleted: removeMessage,
        }
  );

  const liveTeamInboxProps = useMemo(
    () =>
      isHumanAgentInboxLive() && !isBotMessagesPage
        ? {
            teamInboxes,
            activeWorkspaceId,
            onAfterTeamInboxMove: handleAfterTeamInboxMove,
          }
        : {
            teamInboxes: [],
            activeWorkspaceId: null,
            onAfterTeamInboxMove: undefined,
          },
    [teamInboxes, activeWorkspaceId, handleAfterTeamInboxMove, isBotMessagesPage],
  );

  return (
    <AppLayout>
      <div className="flex h-full min-h-0 w-full flex-col">
        <InboxResizableShell
          selectedId={selectedId}
          list={
            <ConversationList
              selectedId={selectedId}
              selectedTicketId={null}
              onSelectConversation={handleSelectConversation}
              onSelectTicket={() => {}}
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
              mode="chats"
              showModeToggle={false}
              panelTitle={isBotMessagesPage ? "Bot Messages" : "Inbox"}
              panelSubtitle={isBotMessagesPage ? "Bot performance review" : "Prioritized queue"}
              conversations={conversations}
              inboxCategories={listViewCategories}
              teamInboxes={isBotMessagesPage ? [] : teamInboxes}
              listLoading={listLoading}
              listError={listError}
              onRetryList={() => void refreshInbox({ silent: true })}
              hasMoreConversations={hasMoreConversations}
              loadingMoreConversations={loadingMoreConversations}
              onLoadMoreConversations={loadMoreConversations}
              activeWorkspaceId={activeWorkspaceId}
              workspaces={workspacePickerList}
              onFoldersChanged={isBotMessagesPage ? undefined : handleFoldersChanged}
            />
          }
          thread={
            <ChatThread
              conversationId={selectedId}
              conversation={conversation}
              messages={messages}
              threadLoading={threadLoading}
              threadError={threadError}
              sendMessage={sendMessage}
              deleteMessage={deleteMessage}
              onRetryThread={() => void reloadThread({ silent: true })}
              transferChat={isBotMessagesPage ? null : transferChat}
              onAfterTransfer={handleAfterTransfer}
              onResolve={handleResolve}
              onReopen={handleReopen}
              onTakeOver={isBotMessagesPage ? handleTakeOver : undefined}
              selectedTicket={null}
              submitTicketWithStatus={null}
              onAfterTicketAction={undefined}
              activeWorkspaceId={liveTeamInboxProps.activeWorkspaceId}
              readOnly={isBotMessagesPage}
            />
          }
          details={
            <UserDetailsPanel
              conversationId={selectedId}
              conversation={conversation}
              conversationLoading={threadLoading}
              createTicketFromLiveChat={isBotMessagesPage ? null : createTicketFromLiveChat}
              onTicketCreated={handleTicketCreated}
              workspaceId={activeWorkspaceId}
              centralInbox
              sessionWorkspaceId={conversation?.workspaceId ?? null}
              onVisitorEmailUpdated={handleVisitorEmailUpdated}
              teamInboxes={liveTeamInboxProps.teamInboxes}
              onAfterTeamInboxMove={liveTeamInboxProps.onAfterTeamInboxMove}
            />
          }
        />
      </div>
    </AppLayout>
  );
}
